import { Badge, Box, Flex, Heading, IconButton, Select, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useMemo, useState } from "react";
import { statusColors } from "typing";
import DashboardTable from "../DashboardTable";

type Table3RowType = {
  id: string;
  name: string;
  sub_area_name: string;
  last_activity: string;
  amount_due: any;
  cumulative_amount_paid: any;
  cumulative_amount_due: any;
  tax_payment_status: string;
  phone_number: string;
  tax_nature_name: string;
  tax_assessments:any[];
};

export default function TaxPayersTable() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [orderSort, setOrderSort] = useState("up")
  const [orderBy, setOrderBy] = useState("")
  const [filterByPayment, setFilterByPayment] = useState("")
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getTaxPayers } = useApi();
  const { data: taxPayers, mutate } = useCache("getTaxPayers", () =>
    getTaxPayers(page, searchBarValue34, 5, "","","","","",filterByPayment, orderBy, orderSort)
  );
  const sortedTaxPayers = useMemo(() => {
    //remove tax payers without tas nature
    // const filteredTaxPayers = taxPayers?.filter((taxPayer: any) => taxPayer.tax_nature_name);
    // if (!filteredTaxPayers) return [];
    return taxPayers
  }, [taxPayers]);
  const handleSearch = (event: any) => {
    const value = event.target.value;
    setSearchBarValue34(value);
    mutate(getTaxPayers(page, value, 5));
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getTaxPayers(page, ""));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getTaxPayers(pg));
  };
  const handleSetNextPage = () => {
    if (taxPayers?.length > 0 && taxPayers[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(getTaxPayers(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (taxPayers?.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getTaxPayers(page + 1));
    }
  };
  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Nom</Heading>
          </Flex>
        ),
        meta: { width: "130px" },
      }),
      table3ColumnHelper.accessor("tax_nature_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Nature de règlement</Heading>
          </Flex>
        ),
        meta: { width: "170px" },
      }),
      table3ColumnHelper.accessor("sub_area_name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Zone</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("phone_number", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {info.getValue<string>()}
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Téléphone</Heading>
          </Flex>
        ),
        meta: { width: "120px" },
      }),
      table3ColumnHelper.accessor("cumulative_amount_due", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Text style={{ textTransform: "capitalize" }}>
                {/* {info.getValue<number>() > 0 ? info.getValue<number>() : info.row.original.cumulative_amount_paid} FCFA */}
                {info.getValue<number>()} FCFA
              </Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Montant dû</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("tax_payment_status", {
        cell: (info) => (
          <Box flex={1}>
            <Flex
              gap="8px"
              alignItems="center"
              px="12px"
              py="0px"
              flexWrap={"wrap"}
              justifyContent={"flex-end"}
            >
              <Badge
                py={1.5}
                px={2}
                colorScheme={statusColors[info.getValue<string>()]}
                fontSize={"12px"}
                rounded={10}
                width={"80px"}
                style={{
                  fontWeight: "lighter",
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {info.getValue<string>() === "paid" ? "À jour" : "Pas à jour"}
              </Badge>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px" justifyContent={"flex-end"}>
            <Heading as="h6">Statut</Heading>
          </Flex>
        ),
        meta: { width: "120px" },
      }),
    ];
  }, []);

  return (
    <>
    <Flex
        w="100%"
        flexDirection="row"
        justifyContent={"flex-end"}
        alignItems={"center"}
        bg={"white"}
        p={"10px"}
        gap={"10px"}
        borderTopRadius={"10px"}
      >
        <Select
          width={"150px"}
          height={"40px"}
          onChange={(e) => {
            const val = e.currentTarget.value
            setFilterByPayment(e.currentTarget.value)
            mutate(getTaxPayers(page, searchBarValue34, 5, "","","","","", val, orderBy, orderSort))
          }}
          fontSize={"12px"}
        >
          <option value={"all"}>Tous les statuts</option>
          <option value={"pending"}>Pas à jour</option>
          <option value={"paid"}>À jour</option>
        </Select>
        <Select
          width={"150px"}
          height={"40px"}
          onChange={(e) =>{
            const val = e.currentTarget.value
            setOrderBy(val)
            mutate(getTaxPayers(page, searchBarValue34, 5, "","","","","", filterByPayment, val, orderSort))
          }}
          fontSize={"12px"}
        >
          <option value={"name"}>Trier par nom</option>
          <option value={"amount"}>Trier par Montant dû</option>
        </Select>
        <IconButton
            variant="outline"
            colorScheme="gray_stroke_0"
            aria-label="Edit"
            size={"sm"}
            height={"40px"}
            width={"40px"}
            icon={orderSort === "up" ? <ArrowUp /> : <ArrowDown/>}
            borderRadius={"4px"}
            onClick={() => {
              const val = orderSort === "up" ? "down" : "up"
              setOrderSort(val)
              mutate(getTaxPayers(page, searchBarValue34, 5, "","","","","",filterByPayment, orderBy, val))
            }}
          />
      </Flex>
      <DashboardTable
        title="Liste des Débiteurs"
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        handleJumpToPage={handleJumpToPage}
        handleSearch={handleSearch}
        handleSetNextPage={handleSetNextPage}
        handleSetPrevPage={handleSetPrevPage}
        handleClearSearch={handleClearSearch}
        tableColumns={table3Columns}
        tableData={sortedTaxPayers}
        searchKey={searchBarValue34}
        setSearchKey={setSearchBarValue34}
      />
    </>
  );
}
